import { yupResolver } from '@hookform/resolvers/yup';
import { type AxiosError } from 'axios';
import { Check, Loader2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';

import { Icons } from '@/assets/icons/dashboard-icons';
import { SuccessIcon } from '@/assets/images';
import { Button, Input } from '@/components/ui';
import { ROUTES } from '@/constants/routes';
import { useResetPassword } from '@/hooks/use-auth';
import { type IPasswordPayload } from '@/types/user';
import {
  passwordRegex,
  passwordRequirementMessage,
  passwordRequirements,
} from '@/utils/helpers';

const passwordSchema = yup
  .object()
  .shape({
    password: yup
      .string()
      .required('Password is a required field')
      .matches(passwordRegex, passwordRequirementMessage),
    confirmPassword: yup
      .string()
      .required('Confirm your password.')
      .test('passwords-match', 'Passwords must match', function (value) {
        return this.parent.password === value;
      }),
  })
  .required();

export default function NewPasswordForm() {
  const navigate = useNavigate();
  const { token } = useParams();
  const [password, setPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const { isPending: isLoading, mutateAsync, isSuccess } = useResetPassword();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<IPasswordPayload>({
    resolver: yupResolver(passwordSchema),
    mode: 'onChange',
  });

  const watchedPassword = watch('password') || '';
  const watchedConfirmPassword = watch('confirmPassword') || '';
  const hasStartedPassword = watchedPassword.length > 0;
  const passwordRequirementStatuses = passwordRequirements.map(requirement => ({
    ...requirement,
    isMet: hasStartedPassword && requirement.isMet(watchedPassword),
  }));
  const isPasswordValid = passwordRequirementStatuses.every(
    requirement => requirement.isMet
  );
  const isConfirmPasswordValid =
    watchedConfirmPassword.length > 0 &&
    watchedConfirmPassword === watchedPassword;
  const canSubmit = isPasswordValid && isConfirmPasswordValid && !isLoading;

  useEffect(() => {
    if (isSuccess) {
      setShowSuccess(true);
      reset();
    }
  }, [isSuccess, reset]);

  // Clear error message after 3 seconds
  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const onSubmit: SubmitHandler<IPasswordPayload> = async ({
    password,
    confirmPassword,
  }) => {
    const newPayload = {
      token: token as string,
      password: password.trim(),
      confirmPassword,
    };
    setErrorMessage(''); // Clear any existing errors

    try {
      await mutateAsync(newPayload);
    } catch (error) {
      // Extract error message from axios error response
      const axiosError = error as AxiosError<{ message?: string }>;
      const message =
        axiosError.response?.data?.message ||
        'An error occurred while resetting your password';
      setErrorMessage(message);
    }
  };
  return (
    <div className="flex flex-col md:flex-row">
      <div className="mx-auto mt-8 h-full w-full max-w-[400px] px-5 py-8 sm:py-20">
        {showSuccess ? (
          <div className="flex flex-col items-center pt-12 sm:pt-24">
            <SuccessIcon className="mb-8" />
            <p className="text-xl font-semibold">Password reset successfully</p>
            <Button
              onClick={() => navigate(ROUTES.LOGIN)}
              type="button"
              className="mt-5 w-full max-w-[318px] text-white"
            >
              Login{' '}
            </Button>
          </div>
        ) : (
          <>
            <h2 className="mb-1 pt-12 text-center text-[20px] font-[500] leading-[47px] sm:pt-24 sm:text-[32px]">
              Create Password
            </h2>
            <p className="text-center text-[16px] leading-[27px]">
              Create a password for your Agentous account with the details you
              have provided
            </p>
            <form onSubmit={handleSubmit(onSubmit)} className="mt-7 w-full">
              <div className="">
                <div className="">
                  <label
                    htmlFor="password"
                    className="mb-1 block text-sm font-medium text-foreground-body"
                  >
                    Create Password
                  </label>
                  <Input
                    className="h-10 w-full"
                    placeholder="Password"
                    autoComplete="new-password"
                    id="password"
                    aria-describedby="password-requirements"
                    aria-invalid={Boolean(errors.password)}
                    {...register('password')}
                    type={password ? 'text' : 'password'}
                    endIcon={
                      password ? (
                        <button
                          type="button"
                          aria-label="Hide password"
                          onClick={() => setPassword(previous => !previous)}
                        >
                          <Icons.PasswordView className="h-5 w-5" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          aria-label="Show password"
                          onClick={() => setPassword(previous => !previous)}
                        >
                          <Icons.PasswordHide className="h-5 w-5" />
                        </button>
                      )
                    }
                  />
                  {hasStartedPassword && (
                    <ul
                      id="password-requirements"
                      className="mt-2 space-y-1"
                      aria-live="polite"
                    >
                      {passwordRequirementStatuses.map(requirement => (
                        <PasswordRequirementRow
                          key={requirement.id}
                          isMet={requirement.isMet}
                          label={requirement.label}
                        />
                      ))}
                    </ul>
                  )}
                </div>
                <div className="mt-5">
                  <label
                    htmlFor="confirmPassword"
                    className="mb-1 block text-sm font-medium text-foreground-body"
                  >
                    Confirm Password
                  </label>
                  <Input
                    className="h-10 w-full"
                    placeholder="Confirm Password"
                    autoComplete="new-password"
                    id="confirmPassword"
                    aria-describedby="confirm-password-requirement"
                    aria-invalid={Boolean(errors.confirmPassword)}
                    {...register('confirmPassword')}
                    type={confirmPassword ? 'text' : 'password'}
                    endIcon={
                      confirmPassword ? (
                        <button
                          type="button"
                          aria-label="Hide confirm password"
                          onClick={() =>
                            setConfirmPassword(previous => !previous)
                          }
                        >
                          <Icons.PasswordView className="h-5 w-5" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          aria-label="Show confirm password"
                          onClick={() =>
                            setConfirmPassword(previous => !previous)
                          }
                        >
                          <Icons.PasswordHide className="h-5 w-5" />
                        </button>
                      )
                    }
                  />
                  {watchedConfirmPassword.length > 0 && (
                    <ul
                      id="confirm-password-requirement"
                      className="mt-2 space-y-1"
                      aria-live="polite"
                    >
                      <PasswordRequirementRow
                        isMet={isConfirmPasswordValid}
                        label="Passwords must match"
                      />
                    </ul>
                  )}
                </div>
                {errorMessage && (
                  <p className="mt-2 pr-8 text-[11px] text-red-800 sm:text-[14px]">
                    {errorMessage}
                  </p>
                )}
                <Button
                  type="submit"
                  disabled={!canSubmit}
                  className="mx-auto mt-6 w-full border border-primary bg-primary text-white disabled:cursor-not-allowed disabled:bg-transparent disabled:text-primary"
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <p className={`text-sm font-medium sm:text-base`}>Next</p>
                  )}
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function PasswordRequirementRow({
  isMet,
  label,
}: {
  isMet: boolean;
  label: string;
}) {
  return (
    <li className="flex items-center gap-2 text-xs leading-snug">
      {isMet ? (
        <Check className="h-3 w-3 shrink-0 text-success-700" />
      ) : (
        <X className="h-3 w-3 shrink-0 text-red-500" />
      )}
      <span className={isMet ? 'text-success-700' : 'text-red-600'}>
        {label}
      </span>
    </li>
  );
}
